import { DeleteOutlined, MessageOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Input, Modal, Popconfirm, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";

import { type TrainerAssistantChat } from "./useTrainerAssistantChat";

// TRE-2313 — historia rozmów jako paleta (⌘K): wpisujesz fragment tytułu, strzałki i Enter.
// Pierwsza pozycja zawsze zaczyna nową rozmowę.

const NEW_CONVERSATION = "__new__";

const formatWhen = (value: string | null) => {
  if (!value) return "";
  const date = dayjs(value);
  if (date.isSame(dayjs(), "day")) return date.format("HH:mm");
  if (date.isSame(dayjs().subtract(1, "day"), "day")) return "wczoraj";
  return date.format(date.isSame(dayjs(), "year") ? "D MMM" : "D MMM YYYY");
};

export const TrainerAssistantHistoryPalette = ({
  chat,
  open,
  onClose,
}: {
  chat: TrainerAssistantChat;
  open: boolean;
  onClose: () => void;
}) => {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActiveIndex(0);
  }, [open]);

  const items = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const sessions = chat.sessions
      .filter((session) => !needle || (session.title ?? "").toLowerCase().includes(needle))
      .map((session) => ({
        id: session.id,
        title: session.title || "Rozmowa",
        when: formatWhen(session.lastMessageAt),
      }));
    return [{ id: NEW_CONVERSATION, title: "Nowa rozmowa", when: "" }, ...sessions];
  }, [chat.sessions, query]);

  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${activeIndex}"]`)?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  // Jedna instancja useChat: przełączenie w trakcie odpowiedzi dopisałoby jej resztę do innej rozmowy.
  const pick = (id: string) => {
    if (chat.busy) return;
    if (id === NEW_CONVERSATION) chat.startNew();
    else chat.selectSession(id);
    onClose();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const offset = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((index) => (index + offset + items.length) % items.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const item = items[activeIndex];
      if (item) pick(item.id);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={560}
      zIndex={1100}
      styles={{ content: { padding: 0, overflow: "hidden" } }}
      destroyOnHidden
    >
      <div className="border-b border-gray-100 px-3 py-2">
        <Input
          autoFocus
          variant="borderless"
          size="large"
          prefix={<SearchOutlined className="text-gray-400" />}
          placeholder="Szukaj w rozmowach"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActiveIndex(0);
          }}
          onKeyDown={handleKeyDown}
        />
      </div>
      {chat.busy ? (
        <div className="border-b border-gray-100 px-4 py-2 text-xs text-gray-500">
          Asystent kończy odpowiedź. Rozmowę zmienisz, gdy skończy.
        </div>
      ) : null}
      <div
        ref={listRef}
        className={`max-h-[50vh] overflow-y-auto p-1.5 ${chat.busy ? "pointer-events-none opacity-50" : ""}`}
      >
        {items.map((item, index) => (
          <div
            key={item.id}
            data-index={index}
            role="option"
            aria-selected={index === activeIndex}
            className={`group flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 ${index === activeIndex ? "bg-gray-100" : ""} ${chat.activeSessionId === item.id ? "font-medium" : ""}`}
            onMouseEnter={() => setActiveIndex(index)}
            onClick={() => pick(item.id)}
          >
            {item.id === NEW_CONVERSATION ? (
              <PlusOutlined className="text-gray-500" />
            ) : (
              <MessageOutlined className="text-gray-400" />
            )}
            <Typography.Text ellipsis className="flex-1">
              {item.title}
            </Typography.Text>
            <span className="text-xs text-gray-400">{item.when}</span>
            {item.id === NEW_CONVERSATION ? null : (
              <Popconfirm
                title="Usunąć rozmowę?"
                okText="Usuń"
                okButtonProps={{ danger: true }}
                cancelText="Anuluj"
                zIndex={1200}
                onPopupClick={(event) => event.stopPropagation()}
                onConfirm={() => chat.deleteSession(item.id)}
              >
                <Button
                  size="small"
                  type="text"
                  danger
                  className="opacity-0 transition-opacity group-hover:opacity-100"
                  icon={<DeleteOutlined />}
                  onClick={(event) => event.stopPropagation()}
                  aria-label="Usuń rozmowę"
                />
              </Popconfirm>
            )}
          </div>
        ))}
        {items.length === 1 && query ? (
          <div className="px-3 py-4 text-center text-sm text-gray-400">Brak rozmów pasujących do wyszukiwania</div>
        ) : null}
      </div>
    </Modal>
  );
};
